import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";

const sectionList = [
  {
    sectionName: "Pain Intensity",
    checkList: [
      `I have no pain at the moment.`,
      `The pain is very mild at this moment.`,
      `The pain is moderate at the moment`,
      `The pain is fairly severe at the moment`,
      `The pain is very severe at the moment`,
      `The pain is the worst imaginable at the moment`,
    ],
    name: "painIntensity",
  },
  {
    sectionName: "Personal Care",
    checkList: [
      `I can look after normally but it causes extra pain.`,
      `I can look after myself normally but it causes extra pain.`,
      `It is painful to look after myself but i am slow and careful.`,
      `I need some help but can manage most of my personal care.`,
      `I need help every day in most aspect of self care.`,
      `I do not get dressed, I wash with difficulty and stay in bed.`,
    ],
    name: "personalCare",
  },
  {
    sectionName: "Lifting",
    checkList: [
      `I can lift heavy without extra pain`,
      `I can lift heavy but it gives extra pain`,
      `Pain prevents me lifting heavy weights off the floor, but i can manage if they are conveniently placed, for example, on a table`,
      `Pain prevent me from lifting heavy weight, but i can manage light to medium weights if they are convineniently positioned`,
      `I can lift very light weights`,
      `I cannot lift or carry anything at all`,
    ],
    name: "liftingInFS",
  },
  {
    sectionName: "Travelling",
    checkList: [
      `I can travel anywhere without pain.`,
      `I can travel anywhere but it gives extra pain`,
      `Pain is bad but I manage journeys over two hours`,
      `Pain restricts me to journeys of less than one hour`,
      `Pain restricts me to short journeys under 30 minutes`,
      `Pain prevents me from traveling except to receive treatment`,
    ],
    name: "travelling",
  },
  {
    sectionName: "Employment/Homemaking",
    checkList: [
      `My normal work/homemaking activities do not cause pain.`,
      `My normal work/homemaking activities increase my pain, but I can still perform all that is required of me.`,
      `I can perform most of my work/homemaking duties, but pain prevents me from performing more physically stressful activities.`,
      `Pain prevents me from doing anything but light duties.`,
      `Pain prevents me from doing even light duties.`,
      `Pain prevents me from performing any work/homemaking duties.`,
    ],
    name: "employmentHomemaking",
  },
  {
    sectionName: "Sitting",
    checkList: [
      `I can sit in any chair as long as I like.`,
      `I can only sit in my favorite chair as long as I like.`,
      `Pain prevents me from sitting more than one hour.`,
      `Pain prevents me from sitting more than 30 minutes`,
      `Pain prevents me from sitting more than 10 minutes`,
      `Pain prevents me from sitting at all`,
    ],
    name: "sittingInFS",
  },
  {
    sectionName: "Standing",
    checkList: [
      `I can stand as long as I want without extra pain.`,
      `I can stand as long as I want but it gives me extra pain`,
      `Pain prevents me from standing for more than 1 hour`,
      `Pain prevents me from standing for more than 30 minutes`,
      `Pain prevents me from standing for more than 10 minutes`,
      `Pain prevents me from standing at all`,
    ],
    name: "standingInFS",
  },
  {
    sectionName: "Sleeping",
    checkList: [
      `My sleep is never disturbed by pain.`,
      `My sleep is occasionally disturbed by pain`,
      `Because of pain I have less than 6 hours of sleep`,
      `Because of pain I have less than 4 hours of sleep`,
      `Because of pain I have less than 2 hours of sleep`,
      `Pain prevents me from sleeping at all`,
    ],
    name: "sleepingInFS",
  },
  {
    sectionName: "Social Life",
    checkList: [
      `My social life is normal and gives me no extra pain.`,
      `My social life is normal but increases the degree of pain.`,
      `Pain has no significant effect on my social life apart from limiting more energetic interests`,
      `Pain has restricted my social life and I do not go out as often.`,
      `Pain has restricted my social life to my home.`,
      `I have no social life because of pain.`,
    ],
    name: "socialLife",
  },
  {
    sectionName: "Walking",
    checkList: [
      `Pain does not prevent me walking any distance.`,
      `Pain prevents me from walking more than 1 mile.`,
      `Pain prevents me from walking more than ½ mile.`,
      `Pain prevents me from walking more than 100 yards`,
      `I can only walk using a stick or crutches`,
      `I am in bed most of the time`,
    ],
    name: "walkingInFS",
  },
];

export const FunctionalScoring = ({
  setPatientMedicationInfo,
  patientMedicationInfo,
}) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [totalScore, setTotalsScore] = useState(patientMedicationInfo.functionalScore || 0);
  const [scores, setScores] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);

  // Initialize scores from existing data
  useEffect(() => {
    const initialScores = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    
    sectionList.forEach((section, index) => {
      const value = patientMedicationInfo[section.name];
      if (value) {
        // Find the index of the selected option in the checkList
        const scoreIndex = section.checkList.findIndex(item => item === value);
        if (scoreIndex !== -1) {
          initialScores[index] = scoreIndex;
        }
      }
    });
    
    setScores(initialScores);
  }, [patientMedicationInfo.painIntensity, patientMedicationInfo.personalCare, patientMedicationInfo.liftingInFS, patientMedicationInfo.travelling, patientMedicationInfo.employmentHomemaking, patientMedicationInfo.sittingInFS, patientMedicationInfo.standingInFS, patientMedicationInfo.sleepingInFS, patientMedicationInfo.socialLife, patientMedicationInfo.walkingInFS]);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  const handleOnChange = (e, score, objectId) => {
    const newScore = [...scores];
    newScore[objectId] = score ? score : 0;
    setScores(newScore);

    const { name, value } = e.target;
    setPatientMedicationInfo({
      ...patientMedicationInfo,
      [name]: value,
    });

    // console.log(totalScore + score);
  };

  useEffect(() => {
    const sum = scores.reduce((acc, curr) => acc + curr, 0);
    setTotalsScore(sum);
    const percentage = Math.round((sum / 50) * 100);
    
    // Save the functional score to parent state
    setPatientMedicationInfo({
      ...patientMedicationInfo,
      functionalScore: sum,
      functionalScorePercentage: percentage,
    });
    
    console.log(`Functional Score: ${sum}/50 (${percentage}%)`);
  }, [scores]);

  const circleRadius = 70;
  /* The code below is calculating the `dashArray` and `dashArrayOffSet` values for a SVG circle
  element. */
  const dashArray = circleRadius * Math.PI * 2;
  const dashArrayOffSet = dashArray - (dashArray * totalScore) / 50;
  return (
    <div className="mb-5">
      <div className="w-full rounded 2xl:p-5 xl:p-5 lg:p-5 md:p-5 p-1.5 flex flex-col 2xl:flex-row xl:flex-row lg:flex-row md:flex-row  justify-between items-center bg-white mx-auto gap-5 2xl:gap-0 xl:gap-0 lg:gap-0 md:gap-0">
        <div className="flex 2xl:w-[60%] xl:w-[60%] lg:w-[60%] md:w-[60%] w-full flex-col gap-4">
          {sectionList.map((item, index) => (
            <>
              <AccordionItem
                key={index}
                index={index}
                title={item.sectionName}
                checkList={item.checkList}
                isOpen={openIndex === index}
                onToggle={handleToggle}
                name={item.name}
                handleOnChange={handleOnChange}
                patientMedicationInfo={patientMedicationInfo}
              />
            </>
          ))}
        </div>
        <div className="2xl:w-[40%] xl:w-[40%] lg:w-[40%] md:w-[40%] w-full flex flex-col justify-center gap-4 items-center">
          <svg className="2xl:w-[10dvw] xl:w-[10dvw] lg:w-[10dvw] md:w-[10dvw] w-[40dvw] 2xl:h-[10dvw] xl:h-[10dvw] lg:h-[10dvw] md:h-[10dvw] h-[40dvw] 2xl:ml-auto xl:ml-auto lg:ml-auto md:ml-auto mx-auto " viewBox="0 0 150 150">
            <circle
              className="fill-none stroke-[#0A1E44]/20"
              cx={150 / 2}
              cy={150 / 2}
              strokeWidth={"5px"}
              r={70}
            />
            <circle
              className="fill-none stroke-[#0A1E44] transition-all duration-200 ease-linear"
              cx={150 / 2}
              cy={150 / 2}
              strokeWidth={"5px"}
              r={70}
              style={{
                strokeDasharray: dashArray,
                strokeDashoffset: dashArrayOffSet,
              }}
              transform={`rotate(180 ${150 / 2} ${150 / 2})`}
            />
            <text
              x="50%"
              y="50%"
              dy="0.3em"
              textAnchor="middle"
              className=" text-[#0A1E44] uppercase font-[500] 2xl:text-[2dvw] xl:text-[2dvw] lg:text-[2dvw] md:text-[2dvw] text-[6dvw] transition-all duration-200 ease-linear"
            >
              {Math.round((totalScore / 50) * 100)}%
            </text>
          </svg>
          {/* <h3 className="text-[#0A1E44] font-[700] text-[1.3dvw] ml-auto">
            Total Score : {totalScore} / 50
          </h3> */}
        </div>
      </div>
    </div>
  );
};

const AccordionItem = ({
  title,
  checkList,
  isOpen,
  index,
  onToggle,
  name,
  handleOnChange,
  patientMedicationInfo,
}) => {
  return (
    <>
      <div className="bg-transparent">
        <button
          style={{
            background: isOpen ? "#24518B" : "transparent",
            color: isOpen ? "#fff" : "#0C0544",
          }}
          className={`flex flex-col justify-start items-start w-full gap-4 py-4 border-b border-[#d4d4d4] ${
            isOpen ? " 2xl:px-8 xl:px-8 lg:px-8 md:px-8 px-4 rounded-full border-b-0" : "2xl:px-4 xl:px-4 lg:px-4 md:px-4 px-4 rounded-none "
          } text-left  focus:outline-none  transition-all duration-300 ease-linear`}
          onClick={() => onToggle(index)}
        >
          <div className="flex justify-between items-center w-full">
            <span
              className={`font-[700] font-mainFont 2xl:text-[1.3vw] xl:text-[1.3vw] lg:text-[1.3vw] md:text-[2.3vw] text-[3.2vw] ${
                isOpen ? "text-[#fff]" : "text-[#333333]"
              }`}
            >
              Section {index + 1} : {title}
            </span>

            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`w-5 h-5 transition-transform duration-500 ${
                isOpen ? "transform rotate-180" : ""
              }`}
            >
              <path
                d="M13.9999 3.33326L1.99994 3.33326C1.87845 3.33364 1.75936 3.36715 1.65549 3.43018C1.55163 3.49321 1.46692 3.58337 1.41048 3.69097C1.35404 3.79856 1.32802 3.9195 1.33521 4.04079C1.3424 4.16207 1.38253 4.27909 1.45128 4.37926L7.45128 13.0459C7.69994 13.4053 8.29861 13.4053 8.54794 13.0459L14.5479 4.37926C14.6174 4.2793 14.6581 4.16222 14.6657 4.04073C14.6733 3.91925 14.6474 3.79801 14.5909 3.69019C14.5344 3.58237 14.4495 3.49209 14.3453 3.42917C14.2411 3.36624 14.1217 3.33307 13.9999 3.33326Z"
                fill={isOpen ? "white" : "#0A1E44"}
              />
            </svg>
          </div>
        </button>
        <div
          className={`overflow-hidden transition-all duration-500 ease-linear ${
            isOpen ? "max-h-96" : "max-h-0"
          }`}
        >
          <div className="2xl:p-6 xl:p-6 lg:p-6 md:p-6 p-2 bg-transparent flex flex-col gap-4">
            {checkList.map((cur, id) => (
              <>
                <div className="flex justify-start items-center gap-3">
                  <input
                    name={name}
                    type="radio"
                    id={`section${index + 1}_${id}`}
                    value={cur}
                    onChange={(e) => handleOnChange(e, id, index)}
                    checked={patientMedicationInfo[name] === cur}
                  />
                  <label
                    htmlFor={`section${index + 1}_${id}`}
                    className="font-mainFont text-[#333333] font-[400] 2xl:text-[1.1vw] xl:text-[1.1vw] lg:text-[1.1vw] md:text-[1.1vw] text-[3.5vw]"
                  >
                    {cur}
                  </label>
                </div>
              </>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
